'use client';

import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, PlaySquare } from 'lucide-react';

type Video = { id: number; name: string; youtubeUrl: string; facebookUrl: string; instagramUrl: string };

function youtubeEmbed(value: string) {
  try {
    const url = new URL(value);
    const id = url.hostname.includes('youtu.be') ? url.pathname.slice(1) : url.searchParams.get('v') ?? url.pathname.split('/').filter(Boolean).at(-1);
    return id ? `https://www.youtube.com/embed/${encodeURIComponent(id)}` : null;
  } catch { return null; }
}

export default function PublicVideoGallery() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void fetch('/api/videos', { cache: 'no-store' })
      .then((response) => response.json())
      .then((data) => setVideos(data.videos ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return <section className="px-4 pb-10 sm:px-7 lg:px-14 xl:px-20">
    <div className="mb-4 flex items-end justify-between gap-3">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-extrabold text-white sm:text-3xl"><PlaySquare className="text-[var(--color-gold)]" /> Video Gallery</h2>
        <p className="mt-1 text-xs text-[#9eafc0]">Videos shared by AETHERA Owner Club achievers.</p>
      </div>
      <button type="button" onClick={() => setExpanded((value) => !value)} className="shrink-0 rounded-lg border border-[var(--color-gold)]/50 px-3 py-2 text-xs font-bold text-[var(--color-gold)] transition hover:bg-[var(--color-gold)]/10" aria-expanded={expanded}>{expanded ? 'Show Less' : 'Show More'}</button>
    </div>

    {loading ? <div className="grid h-52 place-items-center"><Loader2 className="animate-spin text-[var(--color-gold)]" /></div> : videos.length === 0 ? <div className="grid h-44 place-items-center rounded-xl border border-[#244869] bg-[#061428] text-sm text-[#9eafc0]">No Owner Club videos yet.</div> : <div ref={scroller} onScroll={expanded ? undefined : (event) => {
      const element = event.currentTarget; const center = element.scrollLeft + element.clientWidth / 2;
      let closest = 0; let distance = Number.POSITIVE_INFINITY;
      Array.from(element.children).forEach((child, index) => { const card = child as HTMLElement; const next = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center); if (next < distance) { closest = index; distance = next; } });
      setActiveIndex(closest);
    }} className={expanded ? 'grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3' : 'flex snap-x snap-mandatory gap-3 overflow-x-auto px-[12%] py-4 scrollbar-thin sm:gap-4 sm:px-[20%]'}>
      {videos.map((video, index) => {
        const embed = youtubeEmbed(video.youtubeUrl);
        return <article key={video.id} className={`${expanded ? '' : `w-[82%] shrink-0 snap-center transition-all duration-500 sm:w-[60%] ${index === activeIndex ? 'relative z-10 scale-105 border-[var(--color-gold)] shadow-[0_12px_35px_rgba(var(--color-gold-rgb),.24)] opacity-100' : 'scale-90 opacity-55'}`} overflow-hidden rounded-xl border border-[#244869] bg-[#061428]`}>
          {embed && <iframe src={embed} title={`${video.name} Owner Club video`} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="aspect-video w-full bg-black" />}
          <div className="p-3"><p className="truncate text-xs font-bold text-white">{video.name}</p><div className="mt-2 flex gap-2">{[['YouTube', video.youtubeUrl], ['Facebook', video.facebookUrl], ['Instagram', video.instagramUrl]].filter(([, url]) => Boolean(url)).map(([label, url]) => <a key={label} href={url} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-[#244869] px-2 py-2 text-[9px] font-bold text-[#9edcff] transition hover:border-[var(--color-gold)]"><span>{label}</span><ExternalLink size={10} /></a>)}</div></div>
        </article>;
      })}
    </div>}
  </section>;
}
